import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DEFAULT_SHIFT_CONFIG, DrillActivityService, WeekBounds } from 'src/app/services/drill-activity.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { SiteNavigationService } from 'src/app/services/new_services/site-navigation.service';
import { DrillActivityChartComponent } from '../../drill-activity-chart/drill-activity-chart.component';
import { BlastRow, DrillingSession, ShiftConfig, shiftConfigFromDto } from '../../drill-activity-chart/models/drilling.models';
import { ShiftConfigService } from 'src/app/services/shift-config.service';

@Component({
  selector: 'app-drill-activity-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DrillActivityChartComponent],
  templateUrl: './drill-activity-page.component.html',
  styleUrls: ['./drill-activity-page.component.scss'],
})
export class DrillActivityPageComponent implements OnInit {
  private readonly drillActivityService = inject(DrillActivityService);
  private readonly shiftConfigService = inject(ShiftConfigService);
  private readonly utilService = inject(UtilisateurService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly siteNav = inject(SiteNavigationService);
  private readonly route = inject(ActivatedRoute);
  

  protected readonly shiftConfig = signal<ShiftConfig>(DEFAULT_SHIFT_CONFIG);
  protected readonly timeScale = signal<'week' | 'month'>('week');

  private currentBounds: WeekBounds = this.drillActivityService.getCurrentWeekBounds();
  protected readonly startDate = signal<Date>(this.currentBounds.start);
  protected readonly endDate   = signal<Date>(this.currentBounds.end);

  protected readonly blasts = signal<BlastRow[]>([]);

  ngOnInit(): void {
    const siteId = this.utilService.currentSite()?.siteId;
    if (siteId) {
      this.shiftConfigService.getShiftConfig(siteId).subscribe({
        next: dto => this.shiftConfig.set(shiftConfigFromDto(dto)),
        error: () => { /* keep DEFAULT_SHIFT_CONFIG on 404 or error */ },
      });
    }

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      // Hosted by the site Planning tab: ignore the navigation that leaves it (e.g. opening a blast),
      // or the period redirect below would merge `mode`/`date` into the destination URL.
      if (params.get('tab') !== 'planning') {
        return;
      }

      const modeParam = params.get('mode');
      const dateParam = params.get('date');

      if (!this.isValidMode(modeParam) || !dateParam) {
        this.navigateToPeriod(this.timeScale(), this.currentBounds.start, true);
        return;
      }

      const parsedDate = this.parseDate(dateParam);
      if (!parsedDate) {
        this.navigateToPeriod(this.timeScale(), this.currentBounds.start, true);
        return;
      }

      const mode = modeParam;
      const bounds = this.computeBounds(mode, parsedDate);
      const canonicalDate = this.toIsoDateString(bounds.start);

      this.timeScale.set(mode);
      this.currentBounds = bounds;
      this.startDate.set(bounds.start);
      this.endDate.set(bounds.end);

      if (canonicalDate !== dateParam) {
        this.navigateToPeriod(mode, bounds.start, true);
        return;
      }

      this.loadData(bounds);
    });
  }

  protected onDateRangeChange(range: { start: Date; end: Date }): void {
    this.navigateToPeriod(this.resolveModeFromRange(range), range.start);
  }

  private loadData(bounds: WeekBounds): void {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) { return; }

    this.drillActivityService
      .getBlastRowsForWeek(siteId, bounds, this.shiftConfig())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(rows => this.blasts.set(rows));
  }

  protected navigateToBlast(blast: BlastRow): void {
    this.siteNav.navigateFromNodeClick({ id: blast.blastId, type: 'blast' }, 'holes', { view: 'drilling' });
  }

  protected navigateToSession(session: DrillingSession): void {
    const blast = this.blasts().find(b => b.blastId === +session.blastId);
    if (blast) { this.navigateToBlast(blast); }
  }

  private resolveModeFromRange(range: WeekBounds): 'week' | 'month' {
    const days = Math.floor((range.end.getTime() - range.start.getTime()) / 86_400_000) + 1;
    return days > 7 ? 'month' : 'week';
  }

  private computeBounds(mode: 'week' | 'month', date: Date): WeekBounds {
    if (mode === 'month') {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }

    const dow = date.getDay();
    const daysToMonday = dow === 0 ? -6 : 1 - dow;
    const start = new Date(date);
    start.setDate(date.getDate() + daysToMonday);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  private navigateToPeriod(mode: 'week' | 'month', startDate: Date, replaceUrl = false): void {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode, date: this.toIsoDateString(startDate) },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private parseDate(value: string): Date | null {
    const match = /^\d{4}-\d{2}-\d{2}$/.test(value);
    if (!match) {
      return null;
    }

    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toIsoDateString(value: Date): string {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private isValidMode(value: string | null): value is 'week' | 'month' {
    return value === 'week' || value === 'month';
  }
}
